import { Add, CheckCircle, KeyboardDoubleArrowDown, KeyboardDoubleArrowUp, QrCodeScanner } from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { enqueueSnackbar } from "notistack";
import { Fragment, useEffect, useRef, useState } from "react";
import UploadCloud from "src/components/shared/icon/UploadCloud";
import SearchFilterComp from "src/components/shared/SearchFilterComp";
import TabComp from "src/components/shared/TabComp";
import TableComp from "src/components/shared/table/TableComp";
import useAuthGlobal from "src/context/useAuthGlobal";
import useBatchCreate from "src/hooks/batch/useBatchCreate";
import useBatchUpdate from "src/hooks/batch/useBatchUpdate";
import useProductList from "src/hooks/product/useProductList";
import useBatchAction from "./utils/useBatchAction";
import useBatchBody from "./utils/useBatchBody";
import PageHeader from "src/components/shared/PageHeader";
import DateRangePicker from "src/components/shared/DateRangePicker";
import useAppGlobal from "src/context/useAppGlobal";
import ScanItemCodesDialog from "./ScanItemCodesDialog";
import dayjs from "dayjs";
import usePModelList from "src/hooks/product/usePModelList";
import useBrandList from "src/hooks/brands/useBrandList";
import useBatchCount from "src/hooks/batch/useBatchCount";
import useBatchDetail from "src/hooks/batch/useBatchDetail";
import { PERMISSIONS, useRoleCheck } from "src/hooks/permission/permission";
import SearchList from "src/components/shared/SearchList";

const batchSelect = [
  { id: 1, title: "Số lô", value: "code" },
  { id: 2, title: "Sản phẩm", value: "product" },
];

export default function BatchTable() {
  const permissionCheck = useRoleCheck();
  const { handleOpenDialog } = useAppGlobal();
  const { userMe } = useAuthGlobal();

  const [open, setOpen] = useState(true);

  const [batchDetail, setBatchDetail] = useState({
    productId: null,
    productModel: "",
    code: "",
    mfg: null,
    exp: null,
    qty: "",
    warehouse: "",
    file: null,
  });

  const [keyword, setKeyword] = useState();
  const [searchBy, setSearchBy] = useState();
  const [isBrowsed, setIsBrowsed] = useState(false);
  const [sumWhouseId, setSumWhouseId] = useState();
  const [date, setDate] = useState();
  const [prodId, setProdId] = useState();
  const [errorCode, setErrorCode] = useState();
  const [count, setCount] = useState(0);
  const [fileVersion, setFileVersion] = useState(0);

  // Loading state for validating the uploaded file.
  const [isCounting, setIsCounting] = useState(false);

  // Loading state for creating or updating the batch.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputRef = useRef(null);

  const handleClick = () => {
    if (!isCounting && !isSubmitting) {
      inputRef.current?.click();
    }
  };

  const { data: product } = useProductList({
    limit: 200,
    skip: 0,
  });

  const { data: pModelList } = usePModelList(batchDetail.productId?._id ?? batchDetail.productId, {
    limit: 200,
    skip: 0,
  });

  const { trigger: createBatch } = useBatchCreate();
  const { trigger: updateBatch } = useBatchUpdate(batchDetail?.batchId);
  const { trigger: batchCount } = useBatchCount();

  const action = useBatchAction({
    setBatchDetail,
    batchDetail,
  });

  const { data: brands } = useBrandList({
    limit: 200,
    skip: 0,
  });

  const { header, isLoading, error, bodyList, sortData, mutate, setPagi, total, pagi } = useBatchBody({
    warehouseId: userMe?.currentWarehouse?._id,
    batchSelect,
    keyword,
    searchBy,
    date,
    prodId,
    setBatchDetail,
  });

  const handleScan = (item) =>
    handleOpenDialog({
      content: (
        <ScanItemCodesDialog
          mutate={mutate}
          item={item}
          batchDetail={batchDetail}
          product={product?.data}
        />
      ),
      size: "xl",
    });

  const resetFile = () => {
    setBatchDetail((prev) => ({
      ...prev,
      file: null,
    }));

    setIsBrowsed(false);
    setCount(0);

    if (inputRef.current) {
      inputRef.current.value = null;
    }
  };

  const handleCreateBatch = async () => {
    if (isCounting || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const data = new FormData();

      data.append("code", batchDetail.code);
      data.append("mfg", dayjs(batchDetail.mfg).toISOString());
      data.append("exp", dayjs(batchDetail.exp).toISOString());
      data.append("qty", batchDetail.qty?.toString());

      data.append("warehouse", userMe?.currentWarehouse?._id);

      if (batchDetail.file) {
        data.append("file", batchDetail.file);
      }

      data.append("productModel", batchDetail.productModel);

      let res;

      if (!batchDetail.batchId) {
        res = await createBatch({ body: data });
      } else {
        res = await updateBatch({ body: data });
      }

      if (res?.success) {
        if (!batchDetail?.file) {
          handleScan(res?.data);
        }

        setBatchDetail({
          productId: null,
          productModel: "",
          code: "",
          mfg: null,
          exp: null,
          qty: "",
          warehouse: "",
          file: null,
        });

        resetFile();

        enqueueSnackbar("Thành công!", {
          variant: "success",
        });
      } else {
        setErrorCode(res?.error?.code);
        resetFile();

        enqueueSnackbar(res?.error?.message, {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar("Lỗi hệ thống. Vui lòng thử lại sau.", {
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setIsBrowsed(true);

    setBatchDetail((prev) => ({
      ...prev,
      file,
    }));

    setFileVersion((v) => v + 1);
  };

  const countQuantityBatch = async () => {
    if (!batchDetail.file || isCounting) return;

    setIsCounting(true);

    try {
      const data = new FormData();

      data.append("file", batchDetail.file);
      data.append("productModel", batchDetail.productModel);

      const res = await batchCount({
        body: data,
      });

      if (res?.success === true) {
        setCount(res.data.count);
        setErrorCode(undefined);
      } else {
        setErrorCode(res?.error?.code);

        resetFile();

        enqueueSnackbar(res?.error?.message || "Không thể kiểm tra file.", { variant: "error" });
      }
    } catch (err) {
      setErrorCode("UPLOAD_ERROR");

      resetFile();

      enqueueSnackbar("Lỗi hệ thống. Vui lòng thử lại sau.", {
        variant: "error",
      });
    } finally {
      setIsCounting(false);
    }
  };

  useEffect(() => {
    if (batchDetail.file) {
      countQuantityBatch();
    }
  }, [fileVersion]);

  return (
    <Fragment>
      <BatchHeader
        sumWhouseId={sumWhouseId}
        setSumWhouseId={setSumWhouseId}
        open={open}
        setOpen={setOpen}
        userMe={userMe}
        isHide={permissionCheck([PERMISSIONS.CREATE_BATCH_ALL, PERMISSIONS.CREATE_BATCH_OWN])}
      />

      {open && (
        <BatchImport
          batchDetail={batchDetail}
          setBatchDetail={setBatchDetail}
          product={product}
          pModelList={pModelList}
          handleFileChange={handleFileChange}
          handleClick={handleClick}
          inputRef={inputRef}
          isBrowsed={isBrowsed}
          handleCreateBatch={handleCreateBatch}
          errorCode={errorCode}
          count={count}
          isCounting={isCounting}
          isSubmitting={isSubmitting}
        />
      )}

      <Stack
        gap={1.5}
        mb={1.5}
      >
        <TabComp
          tabs={[
            {
              id: "batch",
              name: "Tất cả lô sản phẩm",
            },
            ...(Array.isArray(brands?.data)
              ? brands.data.map((v) => ({
                  ...v,
                  id: v?._id,
                  name: v?.name,
                }))
              : []),
          ]}
        />

        <BatchFilter
          batchSelect={batchSelect}
          setKeyword={setKeyword}
          setSearchBy={setSearchBy}
          date={date}
          setDate={setDate}
          setProdId={setProdId}
          searchBy={searchBy}
        />
      </Stack>

      <BatchList
        header={header}
        bodyList={bodyList}
        sortData={sortData}
        isLoading={isLoading}
        error={error}
        action={action}
        setPagi={setPagi}
        total={total}
        pagi={pagi}
      />
    </Fragment>
  );
}

const BatchHeader = ({ open, setOpen, isHide }) => {
  const permissionCheck = useRoleCheck();

  return (
    <>
      <PageHeader
        name="Quản lý Lô Sản Phẩm"
        subName="Nhập số lô"
      />

      {isHide && (
        <Button
          disabled={!permissionCheck([PERMISSIONS.CREATE_BATCH_OWN, PERMISSIONS.CREATE_BATCH_ALL])}
          sx={{
            display: "flex",
            maxWidth: "180px",
            ml: "auto",
            fontSize: "10px",
          }}
          onClick={() => setOpen(!open)}
          endIcon={!open ? <KeyboardDoubleArrowUp /> : <KeyboardDoubleArrowDown />}
        >
          {!open ? "Ẩn" : "Mở"} nhập số lô sản phẩm
        </Button>
      )}
    </>
  );
};

const BatchImport = ({
  batchDetail,
  count,
  setBatchDetail,
  pModelList,
  handleFileChange,
  handleClick,
  inputRef,
  isBrowsed,
  handleCreateBatch,
  errorCode,
  isCounting,
  isSubmitting,
}) => {
  const permissionCheck = useRoleCheck();

  const { data: batch } = useBatchDetail(batchDetail?.batchId);

  const uploadText = errorCode ? "Tải lại file" : isBrowsed ? "Đã tải lên 1 file" : "Tải lên DS số serial";
  const mfgDate = batchDetail?.mfg && dayjs(batchDetail.mfg).isValid() ? dayjs(batchDetail.mfg) : null;
  const expDate = batchDetail?.exp && dayjs(batchDetail.exp).isValid() ? dayjs(batchDetail.exp) : null;

  if (!permissionCheck([PERMISSIONS.CREATE_BATCH_ALL, PERMISSIONS.CREATE_BATCH_OWN])) {
    return null;
  }

  return (
    <Grid
      container
      spacing={2}
      mt="-16px"
    >
      {/* Product Select */}
      <Grid
        xs={3}
        item
      >
        <SearchList
          size="small"
          params={{
            path: "/api/v1/products",
            params: { isDefault: true },
          }}
          label="Sản phẩm"
          value={batch?.data?.product || batchDetail?.productId || ""}
          onChange={(_, newValue) =>
            setBatchDetail({
              ...batchDetail,
              productId: newValue,
            })
          }
        />
      </Grid>

      {/* Product Model */}
      <Grid
        xs={3}
        item
      >
        <FormControl
          fullWidth
          size="small"
        >
          <InputLabel>Phân loại</InputLabel>

          <Select
            size="small"
            disabled={!batchDetail?.productId || batch?.data?.totalItemCodes > 0 || isCounting || isSubmitting}
            value={batchDetail?.productModel}
            label="Phân loại"
            onChange={(e) =>
              setBatchDetail({
                ...batchDetail,
                productModel: e.target.value,
              })
            }
          >
            {pModelList?.data?.map((val) => (
              <MenuItem
                key={val?._id}
                value={val?._id}
              >
                {val?.name ?? "-"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid
        xs={3}
        item
      >
        <TextField
          size="small"
          label="Số lô"
          disabled={!batchDetail?.productModel || isCounting || isSubmitting}
          fullWidth
          value={batchDetail?.code}
          onChange={(e) =>
            setBatchDetail({
              ...batchDetail,
              code: e.target.value,
            })
          }
        />
      </Grid>

      <Grid
        xs={3}
        item
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Ngày sản xuất"
            value={mfgDate}
            format="DD/MM/YYYY"
            disabled={!batchDetail?.code || isCounting || isSubmitting}
            onChange={(newValue) => {
              const nextMfg = newValue && dayjs(newValue).isValid() ? dayjs(newValue) : null;

              setBatchDetail((prev) => ({
                ...prev,
                mfg: nextMfg,
                exp: null,
                qty: "",
              }));
            }}
            slotProps={{
              textField: {
                size: "small",
              },
            }}
            sx={{ width: "100%" }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid
        xs={3}
        item
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            size="small"
            sx={{ width: "100%" }}
            label="Ngày hết hạn"
            format="DD/MM/YYYY"
            value={expDate}
            disabled={!mfgDate || isCounting || isSubmitting}
            minDate={mfgDate ?? undefined}
            onChange={(newValue) => {
              const nextExp = newValue && dayjs(newValue).isValid() ? dayjs(newValue) : null;

              setBatchDetail((prev) => ({
                ...prev,
                exp: nextExp,
              }));
            }}
            slotProps={{
              textField: {
                size: "small",
              },
            }}
            shouldDisableDate={(date) => !mfgDate || dayjs(date).isBefore(mfgDate, "day")}
          />
        </LocalizationProvider>
      </Grid>

      <Grid
        xs={3}
        item
      >
        <TextField
          size="small"
          type="number"
          label={`Số lượng ${batch?.data?.totalItemCodes > batchDetail?.qty ? "(Nhập số lớn hơn sl cũ)" : ""}`}
          fullWidth
          disabled={!expDate || isCounting || isSubmitting}
          value={batchDetail?.qty ?? ""}
          onChange={(e) =>
            setBatchDetail({
              ...batchDetail,
              qty: +e?.target?.value,
            })
          }
          error={batch?.data?.totalItemCodes > batchDetail?.qty}
        />
      </Grid>

      {/* Upload file */}
      <Grid
        xs={3}
        item
        display="flex"
        alignItems="center"
        gap="12px"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: "none" }}
          disabled={isCounting || isSubmitting}
          onChange={handleFileChange}
        />

        <Button
          size="small"
          fullWidth
          variant="outlined"
          disabled={!batchDetail?.qty || isCounting || isSubmitting}
          startIcon={
            isCounting ? <CircularProgress size={18} /> : !isBrowsed || errorCode ? <UploadCloud color={errorCode ? "var(--error)" : ""} /> : <CheckCircle color="success" />
          }
          onClick={handleClick}
          color={!isBrowsed ? "" : "success"}
          sx={(theme) => ({
            height: "100%",
            borderColor: errorCode ? theme.palette.error.main : theme.palette.primary.main,
            color: errorCode ? theme.palette.error.main : theme.palette.text.primary,
          })}
        >
          {isCounting ? "Đang kiểm tra file..." : uploadText}

          {!isCounting && count > 0 && ` (${count} mã)`}
        </Button>
      </Grid>

      {/* Actions */}
      <Grid
        xs={3}
        item
        display="flex"
        gap="12px"
      >
        <Button
          size="small"
          fullWidth
          variant="outlined"
          disabled={!batchDetail?.qty || !!batchDetail?.file || isCounting || isSubmitting}
          startIcon={<QrCodeScanner />}
          onClick={handleCreateBatch}
        >
          Scan mã sản phẩm
        </Button>
      </Grid>

      <Grid
        xs={12}
        item
      >
        <Button
          size="small"
          fullWidth
          disabled={isCounting || isSubmitting || batch?.data?.totalItemCodes > batchDetail?.qty}
          variant="contained"
          onClick={handleCreateBatch}
          startIcon={
            isCounting || isSubmitting ? (
              <CircularProgress
                size={18}
                color="inherit"
              />
            ) : (
              <Add />
            )
          }
        >
          {isCounting ? "Đang kiểm tra file..." : isSubmitting ? "Đang xử lý..." : `${!!batchDetail?.warehouse ? "Sửa" : "Thêm"} lô sản phẩm`}
        </Button>
      </Grid>

      <Grid
        xs={12}
        mb={2}
      >
        <Divider sx={{ mt: 2 }} />
      </Grid>
    </Grid>
  );
};

const BatchFilter = ({ batchSelect, setKeyword, setSearchBy, date, setDate, setProdId, searchBy }) => {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
    >
      <Box
        display="flex"
        gap="6px"
      >
        <SearchFilterComp
          selectList={batchSelect}
          getSearchKeyword={setKeyword}
          getSelectKeyword={setSearchBy}
          autoComplete={{
            product: {
              limit: 200,
              skip: 0,
              endpoint: "/api/v1/products",
              state: setProdId,
              searchField: "name",
            },
          }}
        />
      </Box>

      <DateRangePicker
        value={date ?? { fromDate: null, toDate: null }}
        onApply={setDate}
        label={!!date ? "" : "Thời gian tạo LOT"}
      />
    </Box>
  );
};

const BatchList = ({ header, bodyList, sortData, isLoading, error, action, setPagi, total, pagi }) => (
  <TableComp
    dynamicWidth="1600px"
    bodyList={bodyList}
    sortData={sortData}
    headers={header}
    isLoading={isLoading}
    error={error}
    action={action}
    setPagi={setPagi}
    total={total}
    pagi={pagi}
  />
);
